import React, { useState } from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, ComposedChart } from 'recharts';
import { getMetaCardTheme } from '../../utils/metaColor';
import ClinicFunnelUnitVisualization from './ClinicFunnelUnitVisualization';
import ClinicFunnelSheetTable from './ClinicFunnelSheetTable';

const fmtBRL = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v) || 0);
const fmtNum = (v) => Number(v || 0).toLocaleString('pt-BR');
const fmtPct = (v) => `${(Number(v) * 100).toFixed(1)}%`;

export default function ClinicFunnelOverviewVisualization({ report, showFunnelAndGoals = true }) {
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [dezenaMetric, setDezenaMetric] = useState('fechamentos'); // 'fechamentos' | 'comparecimentos' | 'agendamentos' | 'leads'

  if (!report) return null;

  if (selectedUnit) {
    return (
      <div>
        <button onClick={() => setSelectedUnit(null)} className="mb-4 text-sm text-primary flex items-center gap-1 hover:underline">
          <span className="material-symbols-outlined text-[16px]">arrow_back</span> Voltar para visão geral
        </button>
        <ClinicFunnelUnitVisualization unit={selectedUnit} report={report} showFunnelAndGoals={showFunnelAndGoals} />
      </div>
    );
  }

  const totals = report.units.reduce((acc, u) => {
    acc.metaReceita += Number(u.metaReceita || 0);
    acc.realizadoReceita += Number(u.realizadoReceita || 0);
    acc.plannedFechamentos += u.plannedFechamentos || 0;
    acc.plannedComparecimentos += u.plannedComparecimentos || 0;
    acc.plannedAgendamentos += u.plannedAgendamentos || 0;
    acc.plannedLeads += u.plannedLeads || 0;
    acc.plannedInvestimento += Number(u.plannedInvestimento || 0);
    acc.realizadoFechamentos += u.realizadoFechamentos || 0;
    acc.realizadoComparecimentos += u.realizadoComparecimentos || 0;
    acc.realizadoAgendamentos += u.realizadoAgendamentos || 0;
    acc.realizadoLeads += u.realizadoLeads || 0;
    acc.realizadoInvestimento += Number(u.realizadoInvestimento || 0);
    return acc;
  }, { 
    metaReceita: 0, realizadoReceita: 0, plannedFechamentos: 0, plannedComparecimentos: 0, plannedAgendamentos: 0, plannedLeads: 0, plannedInvestimento: 0,
    realizadoFechamentos: 0, realizadoComparecimentos: 0, realizadoAgendamentos: 0, realizadoLeads: 0, realizadoInvestimento: 0
  });

  const chartData = report.units.map(u => ({
    name: u.routineName,
    pct: u.plannedFechamentos > 0 ? (u.realizadoFechamentos / u.plannedFechamentos) * 100 : 0
  })).sort((a, b) => b.pct - a.pct);

  // a) Funil Projetado do Grupo (Planejado vs Realizado)
  const funilGrupoData = [
    { etapa: 'Leads', Planejado: totals.plannedLeads, Realizado: totals.realizadoLeads },
    { etapa: 'Agendamentos', Planejado: totals.plannedAgendamentos, Realizado: totals.realizadoAgendamentos },
    { etapa: 'Comparecimentos', Planejado: totals.plannedComparecimentos, Realizado: totals.realizadoComparecimentos },
    { etapa: 'Fechamentos', Planejado: totals.plannedFechamentos, Realizado: totals.realizadoFechamentos },
  ];

  // b) Dezenas do Grupo (D1, D2, D3) agregadas
  const dezenasGrupoMap = {
    D1: { name: 'D1', Planejado: 0, Realizado: 0, BigStretch: 0 },
    D2: { name: 'D2', Planejado: 0, Realizado: 0, BigStretch: 0 },
    D3: { name: 'D3', Planejado: 0, Realizado: 0, BigStretch: 0 },
  };

  report.units.forEach(u => {
    (u.dezenaBreakdown || []).forEach(d => {
      const dKey = d.name || 'D1';
      if (dezenasGrupoMap[dKey]) {
        if (dezenaMetric === 'fechamentos') {
          dezenasGrupoMap[dKey].Planejado += Number(d.plannedFechamentos || 0);
          dezenasGrupoMap[dKey].Realizado += Number(d.realizadoFechamentos || 0);
          dezenasGrupoMap[dKey].BigStretch += Number(d.bigFechamentos || 0);
        } else if (dezenaMetric === 'comparecimentos') {
          dezenasGrupoMap[dKey].Planejado += Number(d.plannedComparecimentos || 0);
          dezenasGrupoMap[dKey].Realizado += Number(d.realizadoComparecimentos || 0);
          dezenasGrupoMap[dKey].BigStretch += Number(d.bigComparecimentos || 0);
        } else if (dezenaMetric === 'agendamentos') {
          dezenasGrupoMap[dKey].Planejado += Number(d.plannedAgendamentos || 0);
          dezenasGrupoMap[dKey].Realizado += Number(d.realizadoAgendamentos || 0);
          dezenasGrupoMap[dKey].BigStretch += Number(d.bigAgendamentos || 0);
        } else if (dezenaMetric === 'leads') {
          dezenasGrupoMap[dKey].Planejado += Number(d.plannedLeads || 0);
          dezenasGrupoMap[dKey].Realizado += Number(d.realizadoLeads || 0);
          dezenasGrupoMap[dKey].BigStretch += Number(d.bigLeads || 0);
        }
      }
    });
  });

  const dezenasGrupoData = Object.values(dezenasGrupoMap);

  // c) Comparecimentos por Unidade
  const comparecimentosUnidadeData = report.units.map(u => ({
    name: u.routineName,
    Planejado: Number(u.plannedComparecimentos || 0),
    Realizado: Number(u.realizadoComparecimentos || 0)
  }));

  // d) Meta e Receita por Unidade (R$)
  const receitaUnidadeData = report.units.map(u => ({
    name: u.routineName,
    Meta: Number(u.metaReceita || 0),
    Realizado: Number(u.realizadoReceita || 0)
  }));

  // e) Investimento por Unidade (R$)
  const investimentoUnidadeData = report.units.map(u => ({
    name: u.routineName,
    Planejado: Number(u.plannedInvestimento || 0),
    Realizado: Number(u.realizadoInvestimento || 0)
  }));

  const getKPI = (label, real, plan, isCurrency = false) => {
    if (!showFunnelAndGoals) {
      return (
        <div className="p-4 rounded-xl border bg-white border-slate-100 shadow-sm">
          <p className="text-[10px] font-bold uppercase text-slate-500 mb-1">{label}</p>
          <div className="flex justify-between items-end">
            <p className="text-2xl font-black text-slate-800">{isCurrency ? fmtBRL(real) : fmtNum(real)}</p>
          </div>
        </div>
      );
    }
    const pct = plan > 0 ? (real / plan) * 100 : 0;
    const theme = getMetaCardTheme(pct);
    return (
      <div className={`p-4 rounded-xl border ${theme.bg} ${theme.border}`}>
        <p className="text-[10px] font-bold uppercase text-slate-500 mb-1">{label}</p>
        <div className="flex justify-between items-end">
          <p className={`text-2xl font-black ${theme.value}`}>{isCurrency ? fmtBRL(real) : fmtNum(real)}</p>
          <p className={`text-xs font-bold ${theme.label}`}>{fmtPct(pct / 100)}</p>
        </div>
        <p className="text-[10px] text-slate-400 mt-1">{isCurrency ? 'Meta: ' + fmtBRL(plan) : 'Meta: ' + fmtNum(plan)}</p>
      </div>
    );
  };

  const getKPICost = (label, real, plan) => {
    if (!showFunnelAndGoals) {
      return (
        <div className="p-4 rounded-xl border bg-white border-slate-100 shadow-sm">
          <p className="text-[10px] font-bold uppercase text-slate-500 mb-1">{label}</p>
          <div className="flex justify-between items-end">
            <p className="text-2xl font-black text-slate-800">{fmtBRL(real)}</p>
          </div>
        </div>
      );
    }
    // Para custos/CPL, menor ou igual ao planejado é bom (inverso de metas de vendas)
    const pct = plan > 0 ? (real / plan) * 100 : 0;
    // se o custo real for menor ou igual à meta planejada, é positivo
    let theme = {
      bg: 'bg-emerald-50 dark:bg-emerald-950/20',
      border: 'border-emerald-200 dark:border-emerald-800/40',
      value: 'text-emerald-700 dark:text-emerald-400',
      label: 'text-emerald-600 dark:text-emerald-400'
    };
    if (plan > 0 && real > plan * 1.15) {
      theme = {
        bg: 'bg-rose-50 dark:bg-rose-950/20',
        border: 'border-rose-200 dark:border-rose-800/40',
        value: 'text-rose-700 dark:text-rose-400',
        label: 'text-rose-600 dark:text-rose-400'
      };
    } else if (plan > 0 && real > plan) {
      theme = {
        bg: 'bg-amber-50 dark:bg-amber-950/20',
        border: 'border-amber-200 dark:border-amber-800/40',
        value: 'text-amber-700 dark:text-amber-400',
        label: 'text-amber-600 dark:text-amber-400'
      };
    }
    return (
      <div className={`p-4 rounded-xl border ${theme.bg} ${theme.border}`}>
        <p className="text-[10px] font-bold uppercase text-slate-500 mb-1">{label}</p>
        <div className="flex justify-between items-end">
          <p className={`text-2xl font-black ${theme.value}`}>{fmtBRL(real)}</p>
          <p className={`text-xs font-bold ${theme.label}`}>{fmtPct(pct / 100)}</p>
        </div>
        <p className="text-[10px] text-slate-400 mt-1">Planejado: {fmtBRL(plan)}</p>
      </div>
    );
  };

  const cplMedioReal = totals.realizadoLeads > 0 ? totals.realizadoInvestimento / totals.realizadoLeads : 0;
  const cplMedioPlan = totals.plannedLeads > 0 ? totals.plannedInvestimento / totals.plannedLeads : 0;

  const custoAgendReal = totals.realizadoAgendamentos > 0 ? totals.realizadoInvestimento / totals.realizadoAgendamentos : 0;
  const custoAgendPlan = totals.plannedAgendamentos > 0 ? totals.plannedInvestimento / totals.plannedAgendamentos : 0;

  const custoCompReal = totals.realizadoComparecimentos > 0 ? totals.realizadoInvestimento / totals.realizadoComparecimentos : 0;
  const custoCompPlan = totals.plannedComparecimentos > 0 ? totals.plannedInvestimento / totals.plannedComparecimentos : 0;

  const cacReal = totals.realizadoFechamentos > 0 ? totals.realizadoInvestimento / totals.realizadoFechamentos : 0;
  const cacPlan = totals.plannedFechamentos > 0 ? totals.plannedInvestimento / totals.plannedFechamentos : 0;

  return (
    <div className="space-y-6">
      {showFunnelAndGoals && report.agencyShare !== null && report.agencyShare !== undefined && (
        <p className="text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded-xl px-4 py-2">
          Metas de leads, agendamentos, comparecimentos e fechamentos calculadas sobre <strong>{`${(Number(report.agencyShare) * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`}</strong> da meta de receita (Agência/CRC); o restante é a trilha Recepção.
        </p>
      )}
      {/* KPIs em 2 Linhas: Financeiro & Volume do Funil */}
      <div className="space-y-4">
        <div>
          <div className="flex items-center gap-1.5 mb-2.5">
            <span className="material-symbols-outlined text-primary text-[18px]">payments</span>
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500">Financeiro & Eficiência</h4>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {getKPI('Receita', totals.realizadoReceita, totals.metaReceita, true)}
            {getKPI('Investimento', totals.realizadoInvestimento, totals.plannedInvestimento, true)}
            {getKPICost('CPL Médio', cplMedioReal, cplMedioPlan)}
            {getKPICost('CAC Médio', cacReal, cacPlan)}
          </div>
        </div>

        <div>
          <div className="flex items-center gap-1.5 mb-2.5">
            <span className="material-symbols-outlined text-primary text-[18px]">filter_alt</span>
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500">Etapas do Funil</h4>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {getKPI('Leads', totals.realizadoLeads, totals.plannedLeads)}
            {getKPI('Agendamentos', totals.realizadoAgendamentos, totals.plannedAgendamentos)}
            {getKPI('Comparecimentos', totals.realizadoComparecimentos, totals.plannedComparecimentos)}
            {getKPI('Fechamentos', totals.realizadoFechamentos, totals.plannedFechamentos)}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
        <h3 className="font-headline font-bold mb-4">Eficiência de Investimento Agregado</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {getKPICost('Custo por Lead (CPL)', cplMedioReal, cplMedioPlan)}
          {getKPICost('Custo por Agendamento', custoAgendReal, custoAgendPlan)}
          {getKPICost('Custo por Comparecimento', custoCompReal, custoCompPlan)}
          {getKPICost('Custo por Fechamento (CAC)', cacReal, cacPlan)}
        </div>
      </div>

      {/* Gráficos da Planilha Excel */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* a) Funil Projetado do Grupo */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
          <h3 className="font-headline font-bold mb-1">Funil Projetado do Grupo</h3>
          <p className="text-xs text-slate-500 mb-4">Comparativo geral por estágio (Planejado vs Realizado)</p>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={funilGrupoData} margin={{ top: 10, right: 10, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="etapa" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <RechartsTooltip formatter={(val) => fmtNum(val)} />
                <Legend />
                {showFunnelAndGoals && <Bar dataKey="Planejado" fill="#94a3b8" radius={[4, 4, 0, 0]} />}
                <Bar dataKey="Realizado" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* b) Dezenas do Grupo com seletor de métrica */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
            <div>
              <h3 className="font-headline font-bold mb-1">Dezenas do Grupo (D1, D2, D3)</h3>
              <p className="text-xs text-slate-500">Evolução temporal agregada do grupo</p>
            </div>
            <div className="inline-flex rounded-lg bg-slate-100 p-0.5 border border-slate-200 text-xs font-semibold">
              <button
                type="button"
                onClick={() => setDezenaMetric('fechamentos')}
                className={`px-2.5 py-1 rounded transition-colors ${dezenaMetric === 'fechamentos' ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-600 hover:text-slate-900'}`}
              >
                Fech.
              </button>
              <button
                type="button"
                onClick={() => setDezenaMetric('comparecimentos')}
                className={`px-2.5 py-1 rounded transition-colors ${dezenaMetric === 'comparecimentos' ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-600 hover:text-slate-900'}`}
              >
                Comp.
              </button>
              <button
                type="button"
                onClick={() => setDezenaMetric('agendamentos')}
                className={`px-2.5 py-1 rounded transition-colors ${dezenaMetric === 'agendamentos' ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-600 hover:text-slate-900'}`}
              >
                Agend.
              </button>
              <button
                type="button"
                onClick={() => setDezenaMetric('leads')}
                className={`px-2.5 py-1 rounded transition-colors ${dezenaMetric === 'leads' ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-600 hover:text-slate-900'}`}
              >
                Leads
              </button>
            </div>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={dezenasGrupoData} margin={{ top: 10, right: 10, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <RechartsTooltip formatter={(val) => fmtNum(val)} />
                <Legend />
                <Bar dataKey="Realizado" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
                {showFunnelAndGoals && <Line type="monotone" dataKey="Planejado" stroke="#64748b" strokeWidth={2} strokeDasharray="4 4" />}
                {showFunnelAndGoals && <Line type="monotone" dataKey="BigStretch" name="Big Stretch" stroke="#10b981" strokeWidth={2} />}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* c) Comparecimentos por Unidade */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
          <h3 className="font-headline font-bold mb-1">Comparecimentos por Unidade</h3>
          <p className="text-xs text-slate-500 mb-4">Volume planejado vs realizado</p>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={comparecimentosUnidadeData} margin={{ top: 10, right: 10, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <RechartsTooltip formatter={(val) => fmtNum(val)} />
                <Legend />
                {showFunnelAndGoals && <Bar dataKey="Planejado" fill="#94a3b8" radius={[4, 4, 0, 0]} />}
                <Bar dataKey="Realizado" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* d) Meta e Receita por Unidade */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
          <h3 className="font-headline font-bold mb-1">Meta e Receita por Unidade (R$)</h3>
          <p className="text-xs text-slate-500 mb-4">Atingimento financeiro de faturamento</p>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={receitaUnidadeData} margin={{ top: 10, right: 10, left: -5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(val) => `R$ ${(val / 1000).toFixed(0)}k`} />
                <RechartsTooltip formatter={(val) => fmtBRL(val)} />
                <Legend />
                {showFunnelAndGoals && <Bar dataKey="Meta" fill="#cbd5e1" radius={[4, 4, 0, 0]} />}
                <Bar dataKey="Realizado" fill="#10b981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* e) Investimento por Unidade */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
          <h3 className="font-headline font-bold mb-1">Investimento por Unidade (R$)</h3>
          <p className="text-xs text-slate-500 mb-4">Verba de mídia planejada vs executada</p>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={investimentoUnidadeData} margin={{ top: 10, right: 10, left: -5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(val) => `R$ ${(val / 1000).toFixed(0)}k`} />
                <RechartsTooltip formatter={(val) => fmtBRL(val)} />
                <Legend />
                {showFunnelAndGoals && <Bar dataKey="Planejado" fill="#cbd5e1" radius={[4, 4, 0, 0]} />}
                <Bar dataKey="Realizado" fill="#f59e0b" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* f) Ranking de Atingimento */}
        {showFunnelAndGoals && (
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
            <h3 className="font-headline font-bold mb-1">Ranking de Atingimento (% Fechamentos)</h3>
            <p className="text-xs text-slate-500 mb-4">Percentual atingido em relação à meta de fechamentos</p>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={(val) => `${val}%`} />
                  <RechartsTooltip formatter={(val) => `${Number(val).toFixed(1)}%`} />
                  <Bar dataKey="pct" name="% Fechamentos" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      <ClinicFunnelSheetTable report={report} showFunnelAndGoals={showFunnelAndGoals} onSelectUnit={setSelectedUnit} />
    </div>
  );
}